import React, { PropsWithChildren, useCallback, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import styles from './styles/layoutContainer.module.scss';
import classnames from 'classnames';
import SideBar from 'components/sidebar';
import NavBar from 'components/navbar';
import useNotifications from 'hooks/useNotifications';
import { IAppState } from 'types';
import {
  NavTitle,
  MarketScanWaitingPageHeader,
} from 'components/navbar/NavBar';
import { ToggleSidebar } from 'actions';
import { useLocation } from 'react-router-dom';
import MobileBottomNav from './components/MobileBottomNav';
import {
  shouldShowMobileNav,
  isMarketScanWaitingPage,
  getLayoutConditions,
} from './utils';
import { isSafari } from 'helpers';
import { selectPolicyScanFromUrlById } from 'selectors/policyScanSelectors';
import TestimonialAnimation from 'containers/market-scan/components/TestimonialAnimation';
import useZoomSupportCheck from 'hooks/useZoomSupportCheck';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';

const LayoutContainer = ({
  children,
  navBar,
}: PropsWithChildren<{}> & { navBar: { navbarTitle?: string } }) => {
  useNotifications();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const policyScan = useSelector(selectPolicyScanFromUrlById);
  const { isZoomWorking } = useZoomSupportCheck();

  // Show/hide mobile sidebar
  const [openMobile, setMobile] = useState(false);

  const {
    adviser,
    layout: { expanded },
    user,
  } = useSelector<IAppState, IAppState>(R.pick(['adviser', 'layout', 'user']));

  const openSidebar = useCallback(() => dispatch(ToggleSidebar(true)), []);
  const closeSidebar = useCallback(() => dispatch(ToggleSidebar(false)), []);

  const openMobileSidebar = useCallback(() => setMobile(true), []);
  const closeMobileSidebar = useCallback(() => setMobile(false), []);

  const showMobileNav = shouldShowMobileNav(pathname);
  const showMSWaitingPageHeader = isMarketScanWaitingPage(pathname);

  const conditions = getLayoutConditions(
    pathname,
    expanded,
    showMSWaitingPageHeader,
    isZoomWorking ?? false,
  );

  return (
    <div
      id="layout-container"
      className={classnames(
        styles.container,
        conditions.expanded && 'quashed-sidebar-open',
        conditions.shouldReducePadding && styles.reducedpaddingContainer,
        conditions.showMSWaitingPageHeader && styles.msWaitingPageContainer,
        conditions.isLifeResultsPage && styles.lifeResultsPageContainer,
        conditions.isLifeResultsPageWithZoom &&
          styles.lifeResultsPageContainerWithZoom,
        conditions.isResultsPageWithZoom && styles.resultsPageContainerWithZoom,
      )}
    >
      {openMobile && !isSafari() && <div className={styles.overlay} />}
      <SideBar
        adviser={adviser}
        user={user}
        small={!expanded}
        mobile={openMobile}
        openSidebar={openSidebar}
        closeSidebar={closeSidebar}
        closeMobileSidebar={closeMobileSidebar}
      />
      <div className={classnames(styles.right)}>
        {showMSWaitingPageHeader && (
          <div className={styles.desktopTestimonial}>
            <TestimonialAnimation />
          </div>
        )}
        <NavBar
          user={user}
          openMobileSidebar={openMobileSidebar}
          navbarTitle={navBar.navbarTitle}
          policyScan={policyScan}
        />
        <div
          id="main"
          className={classnames(
            styles.children,
            policyScan &&
              policyScan.policyType === INSURANCE_TYPES_NAMES.VEHICLE &&
              styles.vehicleMsResults,
          )}
        >
          {!showMSWaitingPageHeader ? (
            <NavTitle
              policyScan={policyScan}
              className={styles.navTitle}
              navbarTitle={navBar.navbarTitle}
            />
          ) : (
            <MarketScanWaitingPageHeader
              className={styles.mobileMSWaitingHeader}
              policyScan={policyScan}
              navbarTitle={navBar.navbarTitle}
            />
          )}
          {children}
        </div>
        <div id="mobile-bottom-nav"></div>
        {showMobileNav && <MobileBottomNav user={user} />}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IAppState) => {
  return {
    navBar: state.navBar,
  };
};

export default connect(mapStateToProps)(LayoutContainer);
